import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Chip,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Alert,
  InputAdornment,
  TablePagination,
  Badge
} from '@mui/material';
import { 
  Save as SaveIcon, 
  Assignment as AssignmentIcon, 
  CloudDownload as DownloadIcon,
  Search as SearchIcon,
  OpenInNew as OpenIcon,
  FilePresent as FileIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Lock as LockIcon,
  RestorePage as RestoreIcon,
  RateReview as FeedbackIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import api from '../services/api';
import { DEFAULT_STUDENT_MAJORS, fetchStudentMajors } from '../constants/majors';

const DEFAULT_MAJORS = Array.from(new Set([
  ...DEFAULT_STUDENT_MAJORS,
  'คณิตศาสตร์ประยุกต์'
])).sort((a, b) => a.localeCompare(b, 'th'));

const SupervisorWorkloadEvaluation = () => {
  const [students, setStudents] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [courseStats, setCourseStats] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('ALL');
  const [selectedYear, setSelectedYear] = useState('ALL');
  const [selectedSemester, setSelectedSemester] = useState('ALL');
  const [selectedMajor, setSelectedMajor] = useState('ALL');
  const [availableMajors, setAvailableMajors] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [reviewFilter, setReviewFilter] = useState<'ALL' | 'RESUBMITTED' | 'PENDING' | 'APPROVED'>('ALL');

  // Inline scores state: { [placementId]: { [itemId]: number } }
  const [scoresState, setScoresState] = useState<Record<string, Record<string, number | string>>>({});
  // Feedback state: { [placementId]: string }
  const [feedbackState, setFeedbackState] = useState<Record<string, string>>({});
  // Dirty rows tracking
  const [modifiedRows, setModifiedRows] = useState<Record<string, boolean>>({});
  // Auto-save draft restored banner state
  const [draftRestoredAlert, setDraftRestoredAlert] = useState(false);
  const location = useLocation();
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Artifact modal & Lightbox preview
  const [artifactModalOpen, setArtifactModalOpen] = useState(false);
  const [selectedStudentForArtifact, setSelectedStudentForArtifact] = useState<any>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState('');
  const [previewFileName, setPreviewFileName] = useState('');

  // Feedback & Revision Modal
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [selectedSubForFeedback, setSelectedSubForFeedback] = useState<any>(null);
  const [selectedItemForFeedback, setSelectedItemForFeedback] = useState<any>(null);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackFile, setFeedbackFile] = useState<File | null>(null);
  const [feedbackStatus, setFeedbackStatus] = useState<'NEEDS_REVISION' | 'APPROVED'>('NEEDS_REVISION');
  const [feedbackSaving, setFeedbackSaving] = useState(false);

  const resolveFileUrl = (url?: string) => {
    if (!url) return '';
    if (window.location.protocol === 'https:' && url.startsWith('http://')) {
      url = url.replace(/^http:\/\//i, 'https://');
    }
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/uploads/')) return `/api/v1${url}`;
    if (!url.startsWith('/')) return `/api/v1/uploads/${url}`;
    return url;
  };

  const isImageFile = (url?: string) => {
    if (!url) return false;
    return /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(url);
  };

  const handleOpenFeedback = (sub: any, itm: any) => {
    setSelectedSubForFeedback(sub);
    setSelectedItemForFeedback(itm);
    setFeedbackComment(sub?.feedback_comment || '');
    setFeedbackFile(null);
    setFeedbackStatus(sub?.review_status === 'APPROVED' ? 'APPROVED' : 'NEEDS_REVISION');
    setFeedbackModalOpen(true);
  };

  const handleSaveFeedback = async () => {
    if (!selectedSubForFeedback) return;
    if (feedbackStatus === 'NEEDS_REVISION' && !feedbackComment.trim() && !feedbackFile) {
      alert('กรุณาระบุข้อเสนอแนะ/คำแนะนำ หรือแนบไฟล์คำแนะนำสำหรับนักศึกษา');
      return;
    }

    try {
      setFeedbackSaving(true);
      const fd = new FormData();
      fd.append('feedback_comment', feedbackComment.trim());
      fd.append('review_status', feedbackStatus);
      if (feedbackFile) {
        fd.append('feedback_file', feedbackFile);
      }

      await api.post(`/workload-evaluations/submissions/${selectedSubForFeedback.id}/feedback`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      alert(feedbackStatus === 'NEEDS_REVISION' ? 'บันทึกคำแนะนำและส่งกลับให้นักศึกษาแก้ไขเรียบร้อยแล้ว' : 'บันทึกการตรวจรับรองภาระงานเรียบร้อยแล้ว');
      setFeedbackModalOpen(false);

      // Refresh student list and local selection
      const updatedList = await fetchStudents();
      if (selectedStudentForArtifact && updatedList && updatedList.length > 0) {
        const found = updatedList.find((s: any) => s.placement_id === selectedStudentForArtifact.placement_id);
        if (found) {
          setSelectedStudentForArtifact(found);
        }
      }
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึกคำแนะนำ');
    } finally {
      setFeedbackSaving(false);
    }
  };

  const handleOpenFile = (url: string, name?: string) => {
    const fullUrl = resolveFileUrl(url);
    if (isImageFile(fullUrl)) {
      setPreviewFileUrl(fullUrl);
      setPreviewFileName(name || 'ภาพหลักฐานภาระงาน');
      setPreviewModalOpen(true);
    } else {
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    }
  };


  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSupervisor = user.role === 'SUPERVISOR';
  const isAdminOrStaff = ['ADMIN', 'STAFF'].includes(user.role);

  const fetchCourses = async () => {
    try {
      const res = await api.get('/courses');
      setCourses(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchCourseStats = async (year: string, sem: string, major?: string) => {
    try {
      const params: any = {};
      if (year && year !== 'ALL') params.academic_year = year;
      if (sem && sem !== 'ALL') params.semester = sem;
      if (major && major !== 'ALL') params.major = major;
      const res = await api.get('/workload-evaluations/course-stats', { params });
      setCourseStats(res.data || {});
    } catch (e) {
      console.error('Failed to fetch course stats:', e);
    }
  };

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (selectedCourse !== 'ALL') params.course_id = selectedCourse;
      if (selectedYear !== 'ALL') params.academic_year = selectedYear;
      if (selectedSemester !== 'ALL') params.semester = selectedSemester;
      if (isAdminOrStaff && selectedMajor !== 'ALL') params.major = selectedMajor;
      if (searchTerm) params.search = searchTerm;

      const res = await api.get('/workload-evaluations/students', { params });
      setStudents(res.data);

      // Initialize inline scores and feedbacks cleanly mapping to item IDs
      const initialScores: Record<string, Record<string, number | string>> = {};
      const initialFeedback: Record<string, string> = {};

      res.data.forEach((st: any) => {
        const rawScores = st.scores_json || {};
        const rowScores: Record<string, number | string> = {};
        const items = (st.items && st.items.length > 0) ? st.items : [];
        if (items.length > 0) {
          items.forEach((itm: any) => {
            if (rawScores[itm.id] !== undefined && rawScores[itm.id] !== null && rawScores[itm.id] !== '') {
              rowScores[itm.id] = rawScores[itm.id];
            } else if (itm.item_code && rawScores[itm.item_code] !== undefined && rawScores[itm.item_code] !== null && rawScores[itm.item_code] !== '') {
              rowScores[itm.id] = rawScores[itm.item_code];
            } else {
              rowScores[itm.id] = '';
            }
          });
        } else {
          Object.assign(rowScores, rawScores);
        }

        initialScores[st.placement_id] = rowScores;
        initialFeedback[st.placement_id] = st.feedback || '';
      });

      // Restore unsaved draft from localStorage if unexpected exit/power loss occurred
      let hasDraftRestored = false;
      const restoredModified: Record<string, boolean> = {};
      try {
        const draftKey = `tpms_workload_draft_${user?.id || 'default'}`;
        let savedDraftStr = localStorage.getItem(draftKey);

        // If local draft is empty, try restoring from Cloud server draft backup
        if (!savedDraftStr) {
          try {
            const cloudDraftRes = await api.get('/workload-evaluations/draft');
            if (cloudDraftRes.data?.draft && cloudDraftRes.data.draft.students) {
              savedDraftStr = JSON.stringify(cloudDraftRes.data.draft);
              localStorage.setItem(draftKey, savedDraftStr);
            }
          } catch (e) {
            // Non-fatal cloud check
          }
        }

        if (savedDraftStr) {
          const draftData = JSON.parse(savedDraftStr);
          const studentDrafts = draftData.students || draftData;
          Object.keys(studentDrafts).forEach(pId => {
            const sDraft = studentDrafts[pId];
            const sScores = sDraft?.scores || sDraft;
            if (sScores && initialScores[pId]) {
              initialScores[pId] = {
                ...initialScores[pId],
                ...sScores
              };
              if (sDraft?.feedback !== undefined) {
                initialFeedback[pId] = sDraft.feedback;
              }
              restoredModified[pId] = true;
              hasDraftRestored = true;
            }
          });
        }
      } catch (e) {
        console.warn('Failed to parse draft from localStorage', e);
      }

      setScoresState(initialScores);
      setFeedbackState(initialFeedback);
      setModifiedRows(restoredModified);
      if (hasDraftRestored) {
        setDraftRestoredAlert(true);
      }
      setPage(0);
      return res.data;
    } catch (err) {
      console.error('Failed to fetch students for workload evaluation:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Draft LocalStorage & Cloud Auto-Save Management
  const getDraftStorageKey = () => `tpms_workload_draft_${user?.id || 'default'}`;

  const saveDraftToStorage = (
    placementId: string, 
    rowScores: Record<string, number | string>,
    studentMeta?: { name?: string; code?: string; major?: string },
    feedbackVal?: string
  ) => {
    try {
      const key = getDraftStorageKey();
      const currentDraftRaw = localStorage.getItem(key);
      let currentDraft: any = { students: {} };
      if (currentDraftRaw) {
        try {
          const parsed = JSON.parse(currentDraftRaw);
          if (parsed && parsed.students) {
            currentDraft = parsed;
          } else if (parsed && typeof parsed === 'object') {
            // Support legacy format
            currentDraft.students = {};
            Object.keys(parsed).forEach(pId => {
              currentDraft.students[pId] = { scores: parsed[pId] };
            });
          }
        } catch (e) {
          currentDraft = { students: {} };
        }
      }

      const st = students.find(s => s.placement_id === placementId);
      const studentName = studentMeta?.name || (st ? `${st.prefix || ''}${st.first_name} ${st.last_name}`.trim() : 'นักศึกษา');
      const studentCode = studentMeta?.code || st?.student_code || '';
      const studentMajor = studentMeta?.major || st?.major || '';

      const currentCourseObj = courses.find(c => c.id === selectedCourse);

      currentDraft.userId = user?.id;
      currentDraft.courseId = selectedCourse;
      currentDraft.courseCode = currentCourseObj?.course_code || '';
      currentDraft.courseName = currentCourseObj?.course_name || '';
      currentDraft.academicYear = selectedYear;
      currentDraft.semester = selectedSemester;
      currentDraft.major = selectedMajor;
      currentDraft.updatedAt = new Date().toISOString();

      currentDraft.students[placementId] = {
        studentName,
        studentCode,
        major: studentMajor,
        scores: rowScores,
        feedback: feedbackVal !== undefined ? feedbackVal : (feedbackState[placementId] || ''),
        updatedAt: new Date().toISOString()
      };

      // 1. Instant save to local storage
      localStorage.setItem(key, JSON.stringify(currentDraft));

      // 2. Debounced save to cloud server
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = setTimeout(async () => {
        try {
          await api.post('/workload-evaluations/draft', {
            course_id: selectedCourse !== 'ALL' ? selectedCourse : null,
            academic_year: selectedYear !== 'ALL' ? selectedYear : null,
            semester: selectedSemester !== 'ALL' ? selectedSemester : null,
            major: selectedMajor !== 'ALL' ? selectedMajor : null,
            draft: currentDraft
          });
        } catch (err) {
          console.warn('Failed to sync draft to server:', err);
        }
      }, 1000);
    } catch (e) {
      console.warn('Failed to save workload draft to localStorage', e);
    }
  };

  const removeDraftFromStorage = (placementId: string) => {
    try {
      const key = getDraftStorageKey();
      const currentDraftRaw = localStorage.getItem(key);
      if (!currentDraftRaw) return;
      let currentDraft: any = JSON.parse(currentDraftRaw);
      if (currentDraft && currentDraft.students) {
        delete currentDraft.students[placementId];
        if (Object.keys(currentDraft.students).length === 0) {
          localStorage.removeItem(key);
          api.delete('/workload-evaluations/draft').catch(() => {});
        } else {
          localStorage.setItem(key, JSON.stringify(currentDraft));
          api.post('/workload-evaluations/draft', { draft: currentDraft }).catch(() => {});
        }
      } else {
        delete currentDraft[placementId];
        if (Object.keys(currentDraft).length === 0) {
          localStorage.removeItem(key);
          api.delete('/workload-evaluations/draft').catch(() => {});
        } else {
          localStorage.setItem(key, JSON.stringify(currentDraft));
        }
      }
    } catch (e) {
      console.warn('Failed to remove workload draft from localStorage', e);
    }
  };

  const clearAllDrafts = async () => {
    try {
      localStorage.removeItem(getDraftStorageKey());
      setDraftRestoredAlert(false);
      try {
        await api.delete('/workload-evaluations/draft');
      } catch (e) {
        // Non-fatal
      }
      fetchStudents();
    } catch (e) {
      console.warn('Failed to clear workload draft', e);
    }
  };

  // Initial load: Fetch system settings to default to current academic year & semester
  useEffect(() => {
    const initPage = async () => {
      try {
        await fetchCourses();
        const settingsRes = await api.get('/settings');
        const settings = settingsRes.data || {};

        const locationState = location.state as any;
        let defYear = settings.current_academic_year ? String(settings.current_academic_year) : '2567';
        let defSem = settings.current_semester ? String(settings.current_semester) : '1';
        let defCourse = 'ALL';
        let defMajor = 'ALL';

        if (locationState?.restoreDraft) {
          if (locationState.courseId) defCourse = locationState.courseId;
          if (locationState.academicYear) defYear = String(locationState.academicYear);
          if (locationState.semester) defSem = String(locationState.semester);
          if (locationState.major) defMajor = String(locationState.major);
        } else {
          // If no location state, check if saved draft indicates an active course
          try {
            const rawDraft = localStorage.getItem(getDraftStorageKey());
            if (rawDraft) {
              const parsed = JSON.parse(rawDraft);
              if (parsed.courseId && parsed.students && Object.keys(parsed.students).length > 0) {
                defCourse = parsed.courseId;
                if (parsed.academicYear) defYear = String(parsed.academicYear);
                if (parsed.semester) defSem = String(parsed.semester);
                if (parsed.major) defMajor = String(parsed.major);
              }
            }
          } catch (e) {
            // Non-fatal
          }
        }

        setSelectedCourse(defCourse);
        setSelectedYear(defYear);
        setSelectedSemester(defSem);
        setSelectedMajor(defMajor);
        fetchCourseStats(defYear, defSem, defMajor);

        try {
          const [mapRes, majorsRes] = await Promise.all([
            api.get('/supervisors/mapping-options').catch(() => ({ data: {} })),
            fetchStudentMajors().catch(() => [])
          ]);
          const mList = mapRes.data?.majors || mapRes.data?.studentMajors || [];
          const combined = Array.from(new Set([...DEFAULT_MAJORS, ...mList, ...(Array.isArray(majorsRes) ? majorsRes : [])]))
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b, 'th'));
          if (combined.length > 0) {
            setAvailableMajors(combined);
          }
        } catch (e) {
          console.warn('Failed to load major options:', e);
        }
      } catch (err) {
        console.error('Failed to load initial settings:', err);
      } finally {
        setInitialized(true);
      }
    };
    initPage();
  }, [isAdminOrStaff, location.state]);

  // When filters change and page is initialized, fetch students and course stats
  useEffect(() => {
    if (!initialized) return;
    fetchStudents();
    fetchCourseStats(selectedYear, selectedSemester, selectedMajor);
  }, [selectedCourse, selectedYear, selectedSemester, selectedMajor, initialized]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('filter') === 'RESUBMITTED') {
      setReviewFilter('RESUBMITTED');
    }
  }, [location.search]);

  // Check if current user is allowed to edit a student's evaluation
  const isStudentEditable = (st?: any) => {
    if (!st) return false;
    if (isAdminOrStaff) return true;
    if (isSupervisor && Number(st.form_is_active) === 0) return false;
    if (!st.evaluation_id) return true;
    return st.evaluator_user_id === user.id;
  };

  // Handle score change for an item with strict max_score & min validation
  const handleScoreChange = (placementId: string, itemId: string, value: string, maxScore: number) => {
    const student = students.find(s => s.placement_id === placementId);
    if (!isStudentEditable(student)) return;

    let numVal: number | string = '';
    if (value !== '') {
      const num = parseFloat(value);
      if (isNaN(num)) {
        numVal = '';
      } else if (num < 0) {
        numVal = 0;
      } else if (num > maxScore) {
        numVal = maxScore; // กรอกตัวเลขในแต่ละช่องจะต้องกรอกได้ไม่เกินคะแนนเต็ม
      } else {
        numVal = num;
      }
    }

    const updatedRow = {
      ...(scoresState[placementId] || {}),
      [itemId]: numVal
    };

    setScoresState(prev => ({
      ...prev,
      [placementId]: updatedRow
    }));

    setModifiedRows(prev => ({
      ...prev,
      [placementId]: true
    }));

    // Auto-save draft to localStorage and cloud backup to protect against unexpected exit/outages
    saveDraftToStorage(placementId, updatedRow, {
      name: `${student?.prefix || ''}${student?.first_name || ''} ${student?.last_name || ''}`.trim(),
      code: student?.student_code || '',
      major: student?.major || ''
    });
  };

  // Calculate live total score strictly based on student's valid items
  const calculateRowTotal = (st: any) => {
    if (!st) return 0;
    const placementId = st.placement_id;
    const rowScores = scoresState[placementId] || {};
    const items = (st.items && st.items.length > 0) ? st.items : currentItems;
    let total = 0;
    items.forEach((itm: any) => {
      const val = rowScores[itm.id];
      const n = typeof val === 'number' ? val : parseFloat(val);
      if (!isNaN(n)) total += n;
    });
    return total;
  };

  // Save single student score
  const handleSaveStudentScore = async (student: any) => {
    if (!isStudentEditable(student)) {
      alert('ไม่สามารถแก้ไขคะแนนของนักศึกษาที่ได้รับการประเมินโดยอาจารย์ท่านอื่นแล้ว');
      return;
    }

    const placementId = student.placement_id;
    const rowScores = scoresState[placementId] || {};
    const feedback = feedbackState[placementId] || '';

    // Validation: Check that no rubric item exceeds max_score
    const items = (student.items && student.items.length > 0) ? student.items : currentItems;
    for (const itm of items) {
      const val = rowScores[itm.id];
      if (val !== undefined && val !== null && val !== '') {
        const num = Number(val);
        if (num > Number(itm.max_score)) {
          alert(`ไม่สามารถบันทึกได้: คะแนนในข้อ "${itm.item_code ? `${itm.item_code} ` : ''}${itm.title}" (${num}) กรอกเกินคะแนนเต็ม (${itm.max_score} คะแนน)`);
          return;
        }
        if (num < 0) {
          alert(`ไม่สามารถบันทึกได้: คะแนนในข้อ "${itm.item_code ? `${itm.item_code} ` : ''}${itm.title}" ต้องไม่น้อยกว่า 0`);
          return;
        }
      }
    }

    try {
      setLoading(true);
      await api.post('/workload-evaluations/submit', {
        placementId,
        scores_json: rowScores,
        feedback
      });

      removeDraftFromStorage(placementId);
      setModifiedRows(prev => ({ ...prev, [placementId]: false }));
      alert(`บันทึกคะแนนภาระงานของ ${student.prefix || ''}${student.first_name} ${student.last_name} เรียบร้อยแล้ว`);
      fetchStudents();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึกคะแนน');
    } finally {
      setLoading(false);
    }
  };

  // Save all modified rows that the user is authorized to edit
  const handleSaveAll = async () => {
    const editablePlacementIds = Object.keys(modifiedRows).filter(id => {
      if (!modifiedRows[id]) return false;
      const st = students.find(s => s.placement_id === id);
      return st && isStudentEditable(st);
    });

    if (editablePlacementIds.length === 0) {
      alert('ไม่มีรายการที่ท่านมีสิทธิ์แก้ไขและมีการเปลี่ยนแปลง');
      return;
    }

    if (!window.confirm(`ยืนยันการบันทึกคะแนนทั้งหมด ${editablePlacementIds.length} รายการ?`)) {
      return;
    }

    // Validation: Check all editable rows before saving
    for (const pId of editablePlacementIds) {
      const st = students.find(s => s.placement_id === pId);
      if (!st) continue;
      const rowScores = scoresState[pId] || {};
      const items = (st.items && st.items.length > 0) ? st.items : currentItems;
      for (const itm of items) {
        const val = rowScores[itm.id];
        if (val !== undefined && val !== null && val !== '') {
          const num = Number(val);
          if (num > Number(itm.max_score)) {
            alert(`ไม่สามารถบันทึกได้: นักศึกษา ${st.prefix || ''}${st.first_name} ${st.last_name} มีคะแนนในข้อ "${itm.item_code ? `${itm.item_code} ` : ''}${itm.title}" (${num}) เกินคะแนนเต็ม (${itm.max_score} คะแนน)`);
            return;
          }
          if (num < 0) {
            alert(`ไม่สามารถบันทึกได้: นักศึกษา ${st.prefix || ''}${st.first_name} ${st.last_name} มีคะแนนในข้อ "${itm.item_code ? `${itm.item_code} ` : ''}${itm.title}" น้อยกว่า 0`);
            return;
          }
        }
      }
    }

    try {
      setLoading(true);
      for (const pId of editablePlacementIds) {
        await api.post('/workload-evaluations/submit', {
          placementId: pId,
          scores_json: scoresState[pId] || {},
          feedback: feedbackState[pId] || ''
        });
        removeDraftFromStorage(pId);
      }
      alert('บันทึกคะแนนทั้งหมดเรียบร้อยแล้ว');
      fetchStudents();
    } catch (err: any) {
      console.error(err);
      alert('เกิดข้อผิดพลาดในการบันทึกบางรายการ');
    } finally {
      setLoading(false);
    }
  };

  // Reset evaluation for Admin/Staff
  const handleResetEvaluation = async (placementId: string, studentName: string) => {
    if (window.confirm(`ยืนยันการรีเซ็ตคะแนนภาระงานของ ${studentName}?`)) {
      try {
        await api.delete(`/workload-evaluations/${placementId}`);
        fetchStudents();
      } catch (err) {
        console.error(err);
        alert('เกิดข้อผิดพลาดในการรีเซ็ต');
      }
    }
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (students.length === 0) {
      alert('ไม่มีข้อมูลสำหรับส่งออก');
      return;
    }

    const sampleItems = currentItems.length > 0 ? currentItems : (students[0]?.items || []);
    const exportData = students.map((st, index) => {
      const isOtherEvaluated = isSupervisor && st.evaluation_id && st.evaluator_user_id !== user.id;
      const row: any = {
        'ที่': index + 1,
        'รหัสนักศึกษา': st.student_code,
        'ชื่อ - สกุล': `${st.prefix || ''}${st.first_name} ${st.last_name}`,
        'หมู่เรียน': st.group_no || '-',
        'สาขาวิชา': st.major,
        'โรงเรียน': st.school_name,
        'วิชา': `${st.course_code} ${st.course_name}`
      };

      sampleItems.forEach((itm: any) => {
        const key = `${itm.item_code || ''} ${itm.title} (${itm.max_score} คะแนน)`.trim();
        if (isOtherEvaluated) {
          row[key] = '-';
        } else {
          const scoreVal = scoresState[st.placement_id]?.[itm.id];
          row[key] = scoreVal !== undefined && scoreVal !== '' ? scoreVal : (st.scores_json?.[itm.id] ?? '');
        }
      });

      row['รวมคะแนน'] = isOtherEvaluated ? 'ประเมินแล้ว' : calculateRowTotal(st);
      row['คะแนนเต็ม'] = st.total_max_score || 0;
      if (st.requires_exam) {
        row['คะแนนสอบประมวลผล'] = st.exam_score !== null && st.exam_score !== undefined
          ? `${Number(st.exam_score).toFixed(1)} / ${st.exam_attempt_points || st.exam_set_max_points || 0}`
          : 'ยังไม่สอบ';
        row['คะแนนรวมสุทธิ (ภาระงาน+สอบ)'] = isOtherEvaluated
          ? '-'
          : `${(calculateRowTotal(st) + (Number(st.exam_score) || 0)).toFixed(1)} / ${(st.total_max_score + (Number(st.exam_attempt_points || st.exam_set_max_points) || 0))}`;
      }
      row['สถานะ'] = isOtherEvaluated ? 'ประเมินแล้ว (โดยอาจารย์ท่านอื่น)' : (st.evaluation_id ? 'ประเมินแล้ว' : 'ยังไม่ประเมิน');

      return row;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'คะแนนภาระงาน');
    const courseCode = students[0]?.course_code || 'Workload';
    XLSX.writeFile(wb, `คะแนนภาระงาน_${courseCode}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Available academic years
  const availableYears = React.useMemo(() => {
    const yearsSet = new Set<string>();
    if (selectedYear && selectedYear !== 'ALL') yearsSet.add(String(selectedYear));
    students.forEach(s => { if (s.academic_year) yearsSet.add(String(s.academic_year)); });
    ['2569', '2568', '2567', '2566', '2565'].forEach(y => yearsSet.add(y));
    return Array.from(yearsSet).sort((a, b) => Number(b) - Number(a));
  }, [students, selectedYear]);

  // Available majors for Admin/Staff
  const resolvedMajors = React.useMemo(() => {
    const set = new Set<string>();
    (availableMajors && availableMajors.length > 0 ? availableMajors : DEFAULT_MAJORS).forEach(m => set.add(m));
    students.forEach(s => { if (s.major && s.major.trim()) set.add(s.major.trim()); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'th'));
  }, [availableMajors, students]);

  // Accessible majors for Supervisor (based on enrolled students and supervisor department)
  const supervisorMajors = React.useMemo(() => {
    const set = new Set<string>();
    students.forEach(s => { if (s.major && s.major.trim()) set.add(s.major.trim()); });
    if (user.department && user.department.trim()) set.add(user.department.trim());
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'th'));
  }, [students, user.department]);

  // Statistics
  const totalCount = students.length;
  const evaluatedCount = students.filter(s => s.evaluation_id).length;
  const pendingCount = totalCount - evaluatedCount;
  const resubmittedStudentsCount = React.useMemo(() => {
    return students.filter(s => s.submissions?.some((sub: any) => sub.review_status === 'RESUBMITTED')).length;
  }, [students]);

  // Filtered students by reviewFilter
  const filteredStudents = React.useMemo(() => {
    let list = students;
    if (reviewFilter === 'RESUBMITTED') {
      list = list.filter(s => s.submissions?.some((sub: any) => sub.review_status === 'RESUBMITTED'));
    } else if (reviewFilter === 'PENDING') {
      list = list.filter(s => !s.evaluation_id);
    } else if (reviewFilter === 'APPROVED') {
      list = list.filter(s => Boolean(s.evaluation_id));
    }
    return list;
  }, [students, reviewFilter]);

  // Active form items for current table header
  const currentItems = React.useMemo(() => {
    if (selectedCourse !== 'ALL') {
      const stWithItems = students.find((s: any) => s.course_id === selectedCourse && s.items && s.items.length > 0);
      if (stWithItems) return stWithItems.items;
    }
    const stWithItems = students.find((s: any) => s.items && s.items.length > 0);
    return stWithItems?.items || [];
  }, [students, selectedCourse]);

  const courseRequiresExam = selectedCourse !== 'ALL' && students.some((s: any) => s.course_id === selectedCourse && (s.requires_exam === 1 || s.requires_exam === true));
  const isSelectedCourseFormInactive = selectedCourse !== 'ALL' && (
    courses.find((c: any) => c.id === selectedCourse)?.workload_form_is_active === 0 ||
    students.some((s: any) => s.course_id === selectedCourse && Number(s.form_is_active) === 0)
  );

  // Paginated students
  const visibleStudents = rowsPerPage > 0 
    ? filteredStudents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) 
    : filteredStudents;

  const modifiedEditableCount = Object.keys(modifiedRows).filter(pid => modifiedRows[pid] && isStudentEditable(students.find(s => s.placement_id === pid))).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center' }}>
            <AssignmentIcon sx={{ mr: 1, color: 'primary.main', fontSize: 32 }} />
            การกรอกคะแนนภาระงานนักศึกษา (Workload Evaluation)
          </Typography>
          <Typography variant="body2" color="textSecondary">
            อาจารย์นิเทศก์กรอกคะแนนภาระงานตามเกณฑ์ของรายวิชา (แยกส่วนกับคะแนนประเมินสมรรถนะ)
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            color="success" 
            startIcon={<DownloadIcon />} 
            onClick={handleExportExcel}
            disabled={students.length === 0}
          >
            ส่งออก Excel
          </Button>
          {selectedCourse !== 'ALL' && (
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<SaveIcon />} 
              onClick={handleSaveAll}
              disabled={modifiedEditableCount === 0}
            >
              บันทึกการเปลี่ยนแปลงทั้งหมด ({modifiedEditableCount})
            </Button>
          )}
        </Box>
      </Box>

      {/* Supervisor Department Notice */}
      {isSupervisor && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          <strong>ข้อกำหนด:</strong> ท่านกำลังเข้าสู่ระบบในฐานะอาจารย์นิเทศก์ ระบบจะแสดงเฉพาะนักศึกษาที่สังกัดสาขาวิชาเดียวกันกับท่านเท่านั้น เพื่อความถูกต้องและป้องกันความผิดพลาดในการประเมิน
        </Alert>
      )}

      {/* Summary KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card 
            elevation={reviewFilter === 'ALL' ? 3 : 1} 
            onClick={() => { setReviewFilter('ALL'); setPage(0); }}
            sx={{ borderRadius: 2, borderLeft: '4px solid #1976d2', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}
          >
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" color="textSecondary">นักศึกษาทั้งหมดในความดูแล</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mt: 0.5 }}>
                {totalCount} <span style={{ fontSize: '1rem', fontWeight: 400 }}>คน</span>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card 
            elevation={reviewFilter === 'APPROVED' ? 3 : 1} 
            onClick={() => { setReviewFilter('APPROVED'); setPage(0); }}
            sx={{ borderRadius: 2, borderLeft: '4px solid #2e7d32', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}
          >
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" color="textSecondary">ประเมินภาระงานแล้ว</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main', mt: 0.5 }}>
                {evaluatedCount} <span style={{ fontSize: '1rem', fontWeight: 400 }}>คน</span>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card 
            elevation={reviewFilter === 'PENDING' ? 3 : 1} 
            onClick={() => { setReviewFilter('PENDING'); setPage(0); }}
            sx={{ borderRadius: 2, borderLeft: '4px solid #ed6c02', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}
          >
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" color="textSecondary">ยังไม่ได้ประเมิน / รอดำเนินการ</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main', mt: 0.5 }}>
                {pendingCount} <span style={{ fontSize: '1rem', fontWeight: 400 }}>คน</span>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card 
            elevation={reviewFilter === 'RESUBMITTED' ? 4 : 1} 
            onClick={() => { setReviewFilter(reviewFilter === 'RESUBMITTED' ? 'ALL' : 'RESUBMITTED'); setPage(0); }}
            sx={{ 
              borderRadius: 2, 
              borderLeft: '4px solid #d32f2f',
              cursor: 'pointer',
              bgcolor: reviewFilter === 'RESUBMITTED' ? '#fef2f2' : (resubmittedStudentsCount > 0 ? '#fff5f5' : 'white'),
              transition: 'all 0.2s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="error.main" sx={{ fontWeight: 700 }}>
                  งานที่รอการตรวจซ้ำ
                </Typography>
                {resubmittedStudentsCount > 0 && (
                  <Chip label="รอตรวจใหม่" size="small" color="error" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
                )}
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main', mt: 0.5 }}>
                {resubmittedStudentsCount} <span style={{ fontSize: '1rem', fontWeight: 400 }}>คน</span>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter and Search Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          <Grid size={{ xs: 12, sm: 6, md: isAdminOrStaff ? 3 : 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="course-select-label">รายวิชาฝึกประสบการณ์</InputLabel>
              <Select
                labelId="course-select-label"
                value={selectedCourse}
                label="รายวิชาฝึกประสบการณ์"
                renderValue={(selected) => {
                  if (selected === 'ALL') return 'ทุกรายวิชา (ทั้งหมด)';
                  const found = courses.find((c: any) => c.id === selected);
                  if (!found) return selected;
                  const stat = courseStats[found.id];
                  const countLabel = stat && stat.total_students > 0 ? ` (${stat.total_students} คน)` : ' (0 คน)';
                  const isInactive = found.workload_form_is_active === 0;
                  return `${found.course_code} ${found.course_name}${countLabel}${isInactive ? ' [ปิดรับประเมิน]' : ''}`;
                }}
                onChange={(e) => {
                  setSelectedCourse(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">
                  <em>ทุกรายวิชา (ทั้งหมด)</em>
                </MenuItem>
                {courses.map((c: any) => {
                  const stat = courseStats[c.id];
                  const hasStudents = stat && stat.total_students > 0 && stat.groups && stat.groups.length > 0;
                  const isFormInactive = c.workload_form_is_active === 0;
                  return (
                    <MenuItem key={c.id} value={c.id} sx={{ p: 0 }}>
                      <Tooltip
                        placement="right"
                        arrow
                        slotProps={{
                          tooltip: {
                            sx: { bgcolor: '#1e293b', boxShadow: 4, p: 1.5, minWidth: 220 }
                          }
                        }}
                        title={
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: 'white', borderBottom: '1px solid rgba(255,255,255,0.2)', pb: 0.5, mb: 0.5 }}>
                              {c.course_code} {c.course_name}
                            </Typography>
                            {hasStudents ? (
                              <>
                                <Typography variant="caption" sx={{ display: 'block', color: '#93c5fd', fontWeight: 600, mb: 0.5 }}>
                                  การกระจายตามหมู่เรียน:
                                </Typography>
                                {stat.groups.map((g: any, gIdx: number) => (
                                  <Typography key={gIdx} variant="caption" sx={{ display: 'block', color: '#f1f5f9', pl: 0.5 }}>
                                    • หมู่เรียน {g.group_no}: <strong>{g.student_count}</strong> คน
                                  </Typography>
                                ))}
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#38bdf8', mt: 0.8, pt: 0.5, borderTop: '1px dashed rgba(255,255,255,0.2)' }}>
                                  รวมนักศึกษา: {stat.total_students} คน
                                </Typography>
                              </>
                            ) : (
                              <Typography variant="caption" sx={{ color: '#f87171', fontWeight: 600, display: 'block' }}>
                                ไม่พบรายชื่อนักศึกษา
                              </Typography>
                            )}
                          </Box>
                        }
                      >
                        <Box sx={{ py: 1, px: 2, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                              {c.course_code} {c.course_name}
                            </Typography>
                            {isFormInactive && (
                              <Chip 
                                label="ปิดรับประเมิน" 
                                size="small" 
                                color="warning" 
                                variant="outlined" 
                                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, borderColor: '#f97316', color: '#c2410c' }} 
                              />
                            )}
                          </Box>
                          {hasStudents ? (
                            <Chip label={`${stat.total_students} คน`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem', ml: 1, color: 'primary.main', borderColor: 'primary.light' }} />
                          ) : (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem', ml: 1 }}>
                              (0 คน)
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel>ปีการศึกษา</InputLabel>
              <Select
                value={selectedYear}
                label="ปีการศึกษา"
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">ทุกปีการศึกษา</MenuItem>
                {availableYears.map((yr: any) => (
                  <MenuItem key={yr} value={String(yr)}>{yr}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel>ภาคเรียน</InputLabel>
              <Select
                value={selectedSemester}
                label="ภาคเรียน"
                onChange={(e) => {
                  setSelectedSemester(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">ทุกภาคเรียน</MenuItem>
                <MenuItem value="1">ภาคเรียนที่ 1</MenuItem>
                <MenuItem value="2">ภาคเรียนที่ 2</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {(isAdminOrStaff || supervisorMajors.length > 1) && (
            <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="workload-major-filter-label">สาขาวิชา</InputLabel>
                <Select
                  labelId="workload-major-filter-label"
                  value={selectedMajor}
                  label="สาขาวิชา"
                  onChange={(e) => {
                    setSelectedMajor(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="ALL">ทุกสาขาวิชา (ทั้งหมด)</MenuItem>
                  {(isAdminOrStaff ? resolvedMajors : supervisorMajors).map((m: string) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: (isAdminOrStaff || supervisorMajors.length > 1) ? 3.5 : 5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="ค้นหารหัสนักศึกษา, ชื่อ-สกุล หรือโรงเรียน..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchStudents(); }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={fetchStudents}>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Quick Status Filter Chips */}
      <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', mr: 0.5 }}>
          ตัวกรองสถานะ:
        </Typography>
        <Chip
          label={`ทั้งหมด (${students.length})`}
          color={reviewFilter === 'ALL' ? 'primary' : 'default'}
          variant={reviewFilter === 'ALL' ? 'filled' : 'outlined'}
          onClick={() => { setReviewFilter('ALL'); setPage(0); }}
          clickable
          sx={{ fontWeight: 600 }}
        />
        <Badge badgeContent={resubmittedStudentsCount} color="error">
          <Chip
            label={`งานที่รอการตรวจซ้ำ (${resubmittedStudentsCount})`}
            color={reviewFilter === 'RESUBMITTED' ? 'error' : 'default'}
            variant={reviewFilter === 'RESUBMITTED' ? 'filled' : 'outlined'}
            onClick={() => { setReviewFilter(reviewFilter === 'RESUBMITTED' ? 'ALL' : 'RESUBMITTED'); setPage(0); }}
            clickable
            sx={{
              fontWeight: 700,
              bgcolor: reviewFilter === 'RESUBMITTED' ? undefined : (resubmittedStudentsCount > 0 ? '#fee2e2' : undefined),
              color: reviewFilter === 'RESUBMITTED' ? undefined : (resubmittedStudentsCount > 0 ? '#b91c1c' : undefined),
              borderColor: resubmittedStudentsCount > 0 ? '#ef4444' : undefined
            }}
          />
        </Badge>
        <Chip
          label={`ยังไม่ได้ประเมิน (${pendingCount})`}
          color={reviewFilter === 'PENDING' ? 'warning' : 'default'}
          variant={reviewFilter === 'PENDING' ? 'filled' : 'outlined'}
          onClick={() => { setReviewFilter('PENDING'); setPage(0); }}
          clickable
          sx={{ fontWeight: 600 }}
        />
        <Chip
          label={`ประเมินแล้ว (${evaluatedCount})`}
          color={reviewFilter === 'APPROVED' ? 'success' : 'default'}
          variant={reviewFilter === 'APPROVED' ? 'filled' : 'outlined'}
          onClick={() => { setReviewFilter('APPROVED'); setPage(0); }}
          clickable
          sx={{ fontWeight: 600 }}
        />
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {draftRestoredAlert && (
        <Alert 
          severity="warning" 
          icon={<RestoreIcon fontSize="inherit" />}
          sx={{ mb: 2, borderRadius: 2, alignItems: 'center' }}
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {selectedCourse !== 'ALL' && modifiedEditableCount > 0 && (
                <Button 
                  color="warning" 
                  variant="contained" 
                  size="small" 
                  onClick={handleSaveAll}
                  sx={{ fontWeight: 700, borderRadius: 1.5 }}
                >
                  บันทึกข้อมูลทั้งหมด ({modifiedEditableCount} คน)
                </Button>
              )}
              <Button color="inherit" size="small" onClick={clearAllDrafts} sx={{ fontWeight: 600 }}>
                ล้างคะแนนฉบับร่าง
              </Button>
            </Box>
          }
        >
          <strong>กู้คืนคะแนนแบบร่างอัตโนมัติเรียบร้อยแล้ว:</strong> ระบบตรวจพบคะแนนที่ท่านกรอกค้างไว้ก่อนหน้า (อาจเกิดจากไฟดับ อินเทอร์เน็ตหลุด หรือออกจากระบบก่อนบันทึก) และได้นำกลับมาแสดงในตารางให้ทันที ท่านสามารถตรวจสอบและกดบันทึกคะแนนได้เลยครับ
        </Alert>
      )}

      {selectedCourse === 'ALL' && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          <strong>กรุณาเลือกรายวิชาเพื่อป้อนคะแนนภาระงาน:</strong> ขณะนี้กำลังแสดงรายชื่อนักศึกษาจากทุกรายวิชา เพื่อความถูกต้องในการกรอกคะแนนตามเกณฑ์ของแต่ละวิชา และไม่ทำให้สับสน กรุณาเลือก <strong>รายวิชาฝึกประสบการณ์</strong> ในตัวกรองด้านบน หรือกดปุ่ม <strong>"เลือกวิชานี้เพื่อกรอกคะแนน"</strong> ในแถวของนักศึกษาที่ต้องการ
        </Alert>
      )}

      {isSelectedCourseFormInactive && (
        <Alert 
          severity="warning" 
          icon={<LockIcon fontSize="inherit" />}
          sx={{ mb: 2, borderRadius: 2, fontWeight: 600, border: '1px solid #fed7aa', bgcolor: '#fffbeb', color: '#9a3412' }}
        >
          <strong>รายวิชานี้ปิดรับการประเมินภาระงานชั่วคราว:</strong> แบบประเมินของรายวิชานี้ถูกตั้งค่าเป็น "ปิดใช้งาน" เนื่องจากยังไม่ถึงช่วงเวลากรอกคะแนน หรือสิ้นสุดระยะเวลากรอกคะแนนแล้ว {isSupervisor ? 'เพื่อป้องกันไม่ให้อาจารย์นิเทศก์กรอกคะแนนผิดวิชา ระบบจึงทำการล็อกช่องกรอกคะแนนชั่วคราว' : '(ผู้ดูแลระบบยังคงสามารถตรวจสอบข้อมูลได้)'} (ทั้งนี้คะแนนสอบประมวลผลความรู้และการส่งงานของนักศึกษายังคงถูกบันทึกและประมวลผลอย่างสมบูรณ์)
        </Alert>
      )}

      {/* Main Workload Evaluation Grid (Styled matching official Excel templates) */}
      <TableContainer component={Paper} sx={{ maxHeight: '72vh', overflow: 'auto', borderRadius: 2 }}>
        <Table stickyHeader size="small">
          <TableHead>
            {selectedCourse === 'ALL' ? (
              <TableRow sx={{ bgcolor: '#f0f4f8' }}>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#e3edf7', width: 50 }} align="center">ที่</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#e3edf7', minWidth: 120 }}>รหัสนักศึกษา</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#e3edf7', minWidth: 180 }}>ชื่อ - สกุล</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#e3edf7', minWidth: 140 }}>สาขาวิชา</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#e3edf7', minWidth: 200 }}>รายวิชาฝึกประสบการณ์</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#e3edf7', minWidth: 180 }}>โรงเรียนฝึกสอน</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#e3edf7', minWidth: 140 }}>สถานะการประเมิน</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#e3edf7', minWidth: 150 }}>การดำเนินการ</TableCell>
              </TableRow>
            ) : (
              <TableRow sx={{ bgcolor: '#f0f4f8' }}>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#e3edf7', width: 45 }} align="center">ที่</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#e3edf7', minWidth: 120 }}>รหัสนักศึกษา</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#e3edf7', minWidth: 180 }}>ชื่อ - สกุล</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#e3edf7', minWidth: 140 }}>สาขาวิชา</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#e3edf7', minWidth: 160 }}>โรงเรียนฝึกสอน</TableCell>

                {/* Workload Item Columns */}
                {currentItems.map((itm: any) => (
                  <TableCell 
                    key={itm.id} 
                    align="center" 
                    sx={{ 
                      fontWeight: 'bold', 
                      bgcolor: Number(itm.max_score) > 0 ? '#e8f5e9' : '#e0f2fe', 
                      borderLeft: '1px solid #c8e6c9',
                      minWidth: 125,
                      maxWidth: 150
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.2 }}>
                      {itm.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: Number(itm.max_score) > 0 ? 'success.dark' : 'info.dark', fontWeight: 700, display: 'block' }}>
                      {Number(itm.max_score) > 0 ? `(${itm.max_score} คะแนน)` : '(ไม่คิดคะแนน)'}
                    </Typography>
                  </TableCell>
                ))}

                <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#fff3e0', minWidth: 100 }}>
                  รวมคะแนนภาระงาน
                </TableCell>
                {courseRequiresExam && (
                  <>
                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#e0f2fe', minWidth: 120 }}>
                      สอบประมวลผล
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#ede7f6', minWidth: 120 }}>
                      คะแนนรวมสุทธิ
                    </TableCell>
                  </>
                )}
                <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', minWidth: 100 }}>
                  หลักฐานงาน
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', minWidth: 120 }}>
                  การจัดการ
                </TableCell>
              </TableRow>
            )}
          </TableHead>

          <TableBody>
            {visibleStudents.map((st: any, idx: number) => {
              const displayIndex = (rowsPerPage > 0 ? page * rowsPerPage : 0) + idx + 1;
              const rowTotal = calculateRowTotal(st);
              const isModified = modifiedRows[st.placement_id];
              const isEvaluated = Boolean(st.evaluation_id);
              const isEvaluatedByMe = isEvaluated && st.evaluator_user_id === user?.id;
              const isEvaluatedByOther = isEvaluated && isSupervisor && st.evaluator_user_id !== user?.id;
              const isFormInactive = Number(st.form_is_active) === 0;
              const isLockedByFormInactive = isSupervisor && isFormInactive;
              const canEditScore = (isAdminOrStaff || !isEvaluated || isEvaluatedByMe) && !isLockedByFormInactive;
              const maxPossible = st.total_max_score || 0;
              const hasSubmissions = st.submissions && st.submissions.length > 0;

              if (selectedCourse === 'ALL') {
                return (
                  <TableRow 
                    key={st.placement_id} 
                    hover
                    sx={{ 
                      bgcolor: isEvaluated ? '#f8fafc' : 'white',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <TableCell align="center" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                      {displayIndex}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      {st.student_code}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      {st.prefix || ''}{st.first_name} {st.last_name}
                      {st.group_no && (
                        <Box sx={{ mt: 0.5 }}>
                          <Chip label={`หมู่ ${st.group_no}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#f8fafc' }} />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                      {st.major}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.85rem' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {st.course_code} {st.course_name}
                      </Typography>
                      {st.total_max_score > 0 && (
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                          (คะแนนเต็ม {st.total_max_score} คะแนน)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                      {st.school_name}
                    </TableCell>
                    <TableCell align="center">
                      {isEvaluated ? (
                        <Chip 
                          label={isEvaluatedByOther ? "ประเมินแล้ว (อาจารย์ท่านอื่น)" : `ประเมินแล้ว (${parseFloat(st.total_score).toFixed(1)}/${maxPossible})`}
                          color="success" 
                          size="small" 
                          variant={isEvaluatedByOther ? "outlined" : "filled"}
                          sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                        />
                      ) : (
                        <Chip 
                          label="ยังไม่ได้ประเมิน" 
                          color="warning" 
                          size="small" 
                          variant="outlined" 
                          sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant={isLockedByFormInactive ? "outlined" : "contained"}
                        color={isLockedByFormInactive ? "warning" : "primary"}
                        startIcon={isLockedByFormInactive ? <LockIcon sx={{ fontSize: 14 }} /> : undefined}
                        onClick={() => {
                          setSelectedCourse(st.course_id);
                          setPage(0);
                        }}
                        sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.5, px: 1.5 }}
                      >
                        {isLockedByFormInactive ? 'ดูรายวิชานี้ (ปิดรับประเมิน)' : 'เลือกวิชานี้เพื่อกรอกคะแนน'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              }

              return (
                <TableRow 
                  key={st.placement_id} 
                  hover
                  sx={{ 
                    bgcolor: isModified ? '#fffde7' : (isEvaluated ? '#fafafa' : 'white'),
                    transition: 'background-color 0.2s'
                  }}
                >
                  <TableCell align="center" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                    {displayIndex}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    {st.student_code}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    {st.prefix || ''}{st.first_name} {st.last_name}
                    {st.group_no && (
                      <Box sx={{ mt: 0.5 }}>
                        <Chip label={`หมู่ ${st.group_no}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#f8fafc' }} />
                      </Box>
                    )}
                    {st.submissions?.some((s: any) => s.review_status === 'RESUBMITTED') && (
                      <Box sx={{ mt: 0.5 }}>
                        <Chip 
                          label="แก้ไขแล้ว รอตรวจซ้ำ" 
                          color="error" 
                          size="small" 
                          sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }} 
                        />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                    {st.major}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                    {st.school_name}
                  </TableCell>

                  {/* Input Cells for each Item */}
                  {currentItems.map((itm: any) => {
                    const currentVal = scoresState[st.placement_id]?.[itm.id] ?? '';
                    const subItem = st.submissions?.find((s: any) => s.item_id === itm.id);

                    return (
                      <TableCell 
                        key={itm.id} 
                        align="center" 
                        sx={{ 
                          borderLeft: '1px solid #e0e0e0',
                          p: 1,
                          bgcolor: isEvaluatedByOther ? '#f8fafc' : 'inherit'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          {Number(itm.max_score) === 0 ? (
                            <Chip 
                              label={subItem?.file_url ? "ส่งหลักฐานแล้ว" : "ไม่คิดคะแนน"} 
                              size="small" 
                              color={subItem?.file_url ? "info" : "default"} 
                              variant="outlined" 
                              sx={{ height: 22, fontSize: '0.7rem' }} 
                            />
                          ) : isEvaluatedByOther ? (
                            <Typography variant="body2" sx={{ color: 'text.disabled', fontWeight: 600, width: 36, textAlign: 'center' }}>
                              -
                            </Typography>
                          ) : (
                            <TextField
                              size="small"
                              type="number"
                              value={currentVal}
                              placeholder="0"
                              disabled={!canEditScore}
                              error={Number(currentVal) > Number(itm.max_score) || Number(currentVal) < 0}
                              slotProps={{
                                htmlInput: {
                                  min: 0, 
                                  max: itm.max_score, 
                                  step: 0.5,
                                  style: { textAlign: 'center', padding: '5px 4px', fontSize: '0.9rem', fontWeight: 600 }
                                }
                              }}
                              sx={{ 
                                width: 68,
                                '& .MuiOutlinedInput-root': {
                                  '&.Mui-error': {
                                    bgcolor: '#fee2e2'
                                  }
                                }
                              }}
                              onChange={(e) => handleScoreChange(st.placement_id, itm.id, e.target.value, itm.max_score)}
                              onBlur={(e) => {
                                const val = parseFloat(e.target.value);
                                if (!isNaN(val)) {
                                  if (val > itm.max_score) {
                                    handleScoreChange(st.placement_id, itm.id, String(itm.max_score), itm.max_score);
                                  } else if (val < 0) {
                                    handleScoreChange(st.placement_id, itm.id, '0', itm.max_score);
                                  }
                                }
                              }}
                            />
                          )}
                          {subItem?.file_url && (
                            <Tooltip title={`ดูหลักฐาน: ${subItem.file_name || 'คลิกเพื่อเปิด'}`}>
                              <IconButton 
                                size="small" 
                                color="primary" 
                                onClick={() => handleOpenFile(subItem.file_url, subItem.file_name)}
                                sx={{ p: 0.5 }}
                              >
                                {isImageFile(subItem.file_url) ? <ViewIcon sx={{ fontSize: 16 }} /> : <OpenIcon sx={{ fontSize: 16 }} />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    );
                  })}

                  {/* Total Score */}
                  <TableCell align="center" sx={{ bgcolor: isEvaluatedByOther ? '#f8fafc' : '#fffbf0' }}>
                    {isEvaluatedByOther ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                        <Chip 
                          label="ประเมินแล้ว" 
                          size="small" 
                          color="success" 
                          variant="filled"
                          sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600 }} 
                        />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                          (โดยอาจารย์ท่านอื่น)
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                          {rowTotal.toFixed(1)} / {maxPossible}
                        </Typography>
                        {isEvaluated ? (
                          <Chip label="ประเมินแล้ว" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                        ) : (
                          <Chip label="รอกรอกคะแนน" size="small" color="default" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                      </>
                    )}
                  </TableCell>

                  {courseRequiresExam && (
                    <>
                      <TableCell align="center" sx={{ bgcolor: '#f0f9ff' }}>
                        {st.exam_score !== null && st.exam_score !== undefined ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0369a1' }}>
                              {Number(st.exam_score).toFixed(1)} / {st.exam_attempt_points || st.exam_set_max_points || 0}
                            </Typography>
                            <Chip
                              label={st.exam_status === 'COMPLETED' ? 'สอบแล้ว' : 'กำลังสอบ'}
                              size="small"
                              color={st.exam_status === 'COMPLETED' ? 'success' : 'warning'}
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          </Box>
                        ) : (
                          <Chip
                            label="ยังไม่สอบ"
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem', color: 'text.secondary' }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ bgcolor: '#f5f3ff' }}>
                        {isEvaluatedByOther ? (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        ) : (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#6d28d9' }}>
                              {(rowTotal + (Number(st.exam_score) || 0)).toFixed(1)} / {(maxPossible + (Number(st.exam_attempt_points || st.exam_set_max_points) || 0))}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              (ภาระงาน + สอบ)
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                    </>
                  )}

                  {/* Artifact Inspection Button */}
                  <TableCell align="center">
                    <Tooltip title="ดูรายการไฟล์หลักฐานที่นักศึกษาส่ง">
                      <Button
                        size="small"
                        variant={st.submissions?.some((s: any) => s.review_status === 'RESUBMITTED') ? "contained" : (hasSubmissions ? "outlined" : "text")}
                        color={st.submissions?.some((s: any) => s.review_status === 'RESUBMITTED') ? "error" : (hasSubmissions ? "info" : "inherit")}
                        startIcon={<FileIcon sx={{ fontSize: 16 }} />}
                        onClick={() => {
                          setSelectedStudentForArtifact(st);
                          setArtifactModalOpen(true);
                        }}
                        sx={{ fontSize: '0.75rem', py: 0.5, fontWeight: st.submissions?.some((s: any) => s.review_status === 'RESUBMITTED') ? 700 : 'normal' }}
                      >
                        {st.submissions?.some((s: any) => s.review_status === 'RESUBMITTED') ? 'รอตรวจซ้ำ' : `${st.submissions?.length || 0} ไฟล์`}
                      </Button>
                    </Tooltip>
                  </TableCell>

                  {/* Action Buttons */}
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                      {isEvaluatedByOther ? (
                        <Tooltip title="ได้รับการประเมินโดยอาจารย์ท่านอื่นแล้ว ไม่สามารถแก้ไขได้">
                          <span>
                            <Button
                              variant="outlined"
                              size="small"
                              disabled
                              sx={{ minWidth: 70, fontSize: '0.75rem', py: 0.5 }}
                            >
                              ประเมินแล้ว
                            </Button>
                          </span>
                        </Tooltip>
                      ) : isLockedByFormInactive ? (
                        <Tooltip title="แบบประเมินภาระงานของรายวิชานี้ปิดใช้งานชั่วคราว (ยังไม่ถึงช่วงเวลากรอกคะแนน หรือสิ้นสุดระยะเวลากรอกคะแนนแล้ว)">
                          <span>
                            <Button
                              variant="outlined"
                              color="warning"
                              size="small"
                              disabled
                              startIcon={<LockIcon sx={{ fontSize: 14 }} />}
                              sx={{ minWidth: 80, fontSize: '0.75rem', py: 0.5, borderColor: '#f97316', color: '#c2410c' }}
                            >
                              ปิดรับประเมิน
                            </Button>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip 
                          title={
                            isEvaluated 
                              ? (isModified 
                                  ? "บันทึกการเปลี่ยนแปลงคะแนนของนักศึกษาคนนี้" 
                                  : "บันทึกคะแนนเรียบร้อยแล้ว (สามารถคลิกพิมพ์แก้ไขคะแนนในตารางได้ตลอดเวลา)")
                              : "บันทึกคะแนนนักศึกษาคนนี้"
                          }
                        >
                          <span>
                            <Button
                              variant={isModified ? "contained" : (isEvaluated ? "outlined" : "contained")}
                              color={isModified ? "warning" : (isEvaluated ? "success" : "primary")}
                              size="small"
                              onClick={() => handleSaveStudentScore(st)}
                              disabled={!isModified && isEvaluated}
                              sx={{ minWidth: 70, fontSize: '0.75rem', py: 0.5 }}
                            >
                              {isModified ? "บันทึก" : (isEvaluated ? "บันทึกแล้ว" : "บันทึก")}
                            </Button>
                          </span>
                        </Tooltip>
                      )}

                      {isAdminOrStaff && isEvaluated && (
                        <Tooltip title="รีเซ็ตคะแนนภาระงาน (Admin/Staff)">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleResetEvaluation(st.placement_id, `${st.prefix || ''}${st.first_name} ${st.last_name}`)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}

            {students.length === 0 && (
              <TableRow>
                <TableCell colSpan={selectedCourse === 'ALL' ? 8 : (8 + currentItems.length)} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  <Typography variant="body1">ไม่พบข้อมูลนักศึกษาที่ตรงตามเงื่อนไข</Typography>
                  {isSupervisor && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      (หากเป็นอาจารย์นิเทศก์ จะแสดงเฉพาะนักศึกษาในสาขาวิชาเดียวกันกับอาจารย์เท่านั้น)
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Bar */}
      <TablePagination
        rowsPerPageOptions={[25, 50, 100, { label: 'ทั้งหมด', value: -1 }]}
        component={Paper}
        count={students.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        labelRowsPerPage="จำนวนแถวต่อหน้า:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count !== -1 ? count : `มากกว่า ${to}`}`}
        sx={{ mt: 1.5, borderRadius: 2 }}
      />

      {/* Student Artifacts Modal */}
      <Dialog 
        open={artifactModalOpen} 
        onClose={() => setArtifactModalOpen(false)} 
        fullWidth 
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 700, bgcolor: 'primary.50' }}>
          หลักฐานและไฟล์ภาระงานของ: {selectedStudentForArtifact?.prefix || ''}{selectedStudentForArtifact?.first_name} {selectedStudentForArtifact?.last_name} ({selectedStudentForArtifact?.student_code})
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              วิชา: <strong>{selectedStudentForArtifact?.course_code} {selectedStudentForArtifact?.course_name}</strong> | โรงเรียน: <strong>{selectedStudentForArtifact?.school_name}</strong>
            </Typography>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ bgcolor: 'grey.100' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 70 }}>รหัส</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>รายการภาระงาน</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 90 }}>คะแนนเต็ม</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>ไฟล์หลักฐานที่ส่ง</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 120 }}>วันที่ส่ง</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, width: 180 }}>เปิดดู / ให้คำแนะนำ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedStudentForArtifact?.items?.map((itm: any) => {
                  const sub = selectedStudentForArtifact.submissions?.find((s: any) => s.item_id === itm.id);
                  return (
                    <TableRow key={itm.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{itm.item_code || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{itm.title}</Typography>
                        {itm.description && (
                          <Typography variant="caption" color="textSecondary">{itm.description}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {Number(itm.max_score) > 0 ? (
                          <Chip label={`${itm.max_score} คะแนน`} size="small" color="primary" variant="outlined" />
                        ) : (
                          <Chip label="ไม่คิดคะแนน" size="small" color="info" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        {sub?.file_url ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FileIcon color="primary" fontSize="small" />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {sub.file_name || 'ไฟล์แนบภาระงาน'}
                            </Typography>
                          </Box>
                        ) : (
                          <Chip label="ยังไม่ส่งไฟล์" size="small" color="default" sx={{ fontSize: '0.75rem' }} />
                        )}
                        {sub?.notes && (
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                            หมายเหตุ: {sub.notes}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                        {sub?.submitted_at ? new Date(sub.submitted_at).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {sub?.file_url ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={() => handleOpenFile(sub.file_url, sub.file_name)}
                                startIcon={isImageFile(sub.file_url) ? <ViewIcon sx={{ fontSize: 14 }} /> : <OpenIcon sx={{ fontSize: 14 }} />}
                                sx={{ fontSize: '0.72rem', py: 0.2 }}
                              >
                                {isImageFile(sub.file_url) ? 'รูปภาพ' : 'เปิดดู'}
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color={sub.review_status === 'NEEDS_REVISION' ? 'warning' : 'info'}
                                onClick={() => handleOpenFeedback(sub, itm)}
                                startIcon={<FeedbackIcon sx={{ fontSize: 14 }} />}
                                sx={{ fontSize: '0.72rem', py: 0.2 }}
                              >
                                ให้คำแนะนำ
                              </Button>
                            </Box>
                            {sub.review_status === 'RESUBMITTED' && (
                              <Chip 
                                label={`แก้ไขแล้ว รอตรวจซ้ำ (รอบที่ ${sub.revision_count || 2})`} 
                                color="error" 
                                size="small" 
                                sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700 }} 
                              />
                            )}
                            {sub.review_status === 'NEEDS_REVISION' && (
                              <Chip 
                                label={`ส่งกลับให้นักศึกษาแก้ไข (รอบที่ ${sub.revision_count || 1})`} 
                                color="warning" 
                                size="small" 
                                sx={{ height: 20, fontSize: '0.68rem' }} 
                              />
                            )}
                            {sub.review_status === 'APPROVED' && (
                              <Chip 
                                label="ผ่านการตรวจแล้ว (Approved)" 
                                color="success" 
                                size="small" 
                                sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }} 
                              />
                            )}
                            {(sub.review_status === 'SUBMITTED' || !sub.review_status) && (
                              <Chip 
                                label={`รออาจารย์ตรวจ (รอบที่ ${sub.revision_count || 1})`} 
                                color="info" 
                                size="small" 
                                sx={{ height: 20, fontSize: '0.68rem' }} 
                              />
                            )}
                            {sub.feedback_comment && (
                              <Typography variant="caption" sx={{ color: 'warning.dark', fontSize: '0.7rem', display: 'block', maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                คำแนะนำ: {sub.feedback_comment}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setArtifactModalOpen(false)}>ปิดหน้าต่าง</Button>
        </DialogActions>
      </Dialog>

      {/* Feedback & Revision Dialog */}
      <Dialog open={feedbackModalOpen} onClose={() => !feedbackSaving && setFeedbackModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: feedbackStatus === 'NEEDS_REVISION' ? 'warning.main' : 'info.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <FeedbackIcon />
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
            {feedbackStatus === 'NEEDS_REVISION' 
              ? `ให้คำแนะนำและส่งกลับแก้ไข (รอบที่ ${selectedSubForFeedback?.revision_count || 1})` 
              : 'ตรวจรับรองภาระงาน (Approved)'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              นักศึกษา: {selectedStudentForArtifact?.prefix || ''}{selectedStudentForArtifact?.first_name} {selectedStudentForArtifact?.last_name} ({selectedStudentForArtifact?.student_code})
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
              รายการภาระงาน: <strong>{selectedItemForFeedback?.title}</strong> ({selectedItemForFeedback?.max_score} คะแนน)
            </Typography>
            {selectedSubForFeedback?.file_url && (
              <Box sx={{ mt: 1, p: 1, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                  <FileIcon color="primary" fontSize="small" />
                  <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedSubForFeedback.file_name || 'ไฟล์งานของนักศึกษา'}
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={() => handleOpenFile(selectedSubForFeedback.file_url, selectedSubForFeedback.file_name)}>
                  เปิดดู
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              ผลการตรวจ / การดำเนินการ:
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant={feedbackStatus === 'NEEDS_REVISION' ? 'contained' : 'outlined'}
                color="warning"
                onClick={() => setFeedbackStatus('NEEDS_REVISION')}
                sx={{ flex: 1, py: 1, fontWeight: 700 }}
              >
                ส่งกลับให้นักศึกษาแก้ไข (Need Revision)
              </Button>
              <Button
                variant={feedbackStatus === 'APPROVED' ? 'contained' : 'outlined'}
                color="success"
                onClick={() => setFeedbackStatus('APPROVED')}
                sx={{ flex: 1, py: 1, fontWeight: 700 }}
              >
                ผ่านการตรวจรับรอง (Approved)
              </Button>
            </Box>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={4}
            label={feedbackStatus === 'NEEDS_REVISION' ? "ข้อเสนอแนะ / คำแนะนำในการปรับปรุงแก้ไข *" : "ข้อเสนอแนะ / ความคิดเห็นเพิ่มเติม"}
            placeholder="พิมพ์คำแนะนำ เช่น บทคัดย่อต้องปรับปรุงวัตถุประสงค์ หรือ บทความวิจัยต้องจัดหน้าตามรูปแบบ..."
            value={feedbackComment}
            onChange={(e) => setFeedbackComment(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Box sx={{ p: 2, bgcolor: '#fbfdff', border: '1px dashed #90caf9', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              แนบไฟล์คำแนะนำ / ตรวจแก้ (Annotated File) (ถ้ามี):
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
              (รองรับไฟล์ Word, PDF หรือภาพ ไม่เกิน 50MB เช่น ไฟล์งานวิจัยที่มีคอมเมนต์ตรวจแก้)
            </Typography>
            <input 
              type="file" 
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setFeedbackFile(e.target.files[0]);
                }
              }}
              style={{ display: 'block', fontSize: '0.85rem' }}
            />
            {feedbackFile && (
              <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700, display: 'block', mt: 0.5 }}>
                เลือกแล้ว: {feedbackFile.name}
              </Typography>
            )}
            {selectedSubForFeedback?.feedback_file_url && !feedbackFile && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="textSecondary">
                  ไฟล์คำแนะนำเดิม: {selectedSubForFeedback.feedback_file_name || 'ไฟล์คำแนะนำ'}
                </Typography>
                <Button size="small" variant="text" onClick={() => handleOpenFile(selectedSubForFeedback.feedback_file_url, selectedSubForFeedback.feedback_file_name)}>
                  ดูไฟล์เดิม
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setFeedbackModalOpen(false)} disabled={feedbackSaving}>
            ยกเลิก
          </Button>
          <Button 
            variant="contained" 
            color={feedbackStatus === 'NEEDS_REVISION' ? 'warning' : 'success'}
            onClick={handleSaveFeedback}
            disabled={feedbackSaving}
            sx={{ fontWeight: 700 }}
          >
            {feedbackSaving ? 'กำลังบันทึก...' : (feedbackStatus === 'NEEDS_REVISION' ? 'ยืนยันการส่งกลับแก้ไข' : 'ผ่าน (Approved) จบการตรวจ')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lightbox / Image Preview Dialog */}
      <Dialog 
        open={previewModalOpen} 
        onClose={() => setPreviewModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {previewFileName}
          </Typography>
          <IconButton size="small" onClick={() => setPreviewModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ textAlign: 'center', bgcolor: '#121212', p: 1 }}>
          <img 
            src={previewFileUrl} 
            alt={previewFileName}
            style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: 4 }} 
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            variant="outlined" 
            color="primary" 
            href={previewFileUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            startIcon={<OpenIcon />}
          >
            เปิดในแท็บใหม่ / ดาวน์โหลด
          </Button>
          <Button variant="contained" onClick={() => setPreviewModalOpen(false)}>
            ปิด
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupervisorWorkloadEvaluation;
